import { Widget } from "@foretagsplatsen/widgetjs";
import ImageAction from "../../../models/wysiwygEditor/actions/ImageAction.js";
import WysiwygEditorWidget from "../../../core/widgets/WysiwygEditorWidget/WysiwygEditorWidget.js";

/**
 * `PeriodReportCommentEditorWidget` displays a comment inside a rich text
 * editor.  When an avatar url is set, the avatar is displayed.
 *
 * @param {function} spec.accessor - Function used to get the comment.
 * @param {function} spec.mutator - Function used to set the comment.
 * @param {PeriodReportDefaultText[]} [spec.defaultTexts] - When provided, a
 * dropdown is added in the toolbar to quickly insert one of the default texts.
 * @param {string} [spec.avatarUrl] - Path of the avatar to be displayed.
 * @param {boolean} [spec.showAvatar=false] - When true, the avatar is displayed.
 * @param {InputValueActionBuilder} inputValueActionBuilder - Builder for InputValueAction passed
 *   to the WYSIWYG editor
 * @param {function(File):Promise<string>} [onPictureUploadRequest] - Function invoked with
 *   a File to upload to the backend. It must resolve to the URL of the uploaded file
 * @param {function(string):Promise<void>} [onSourceDeletionRequest] - Function invoked
 *   with the URL of a file to delete from its storage
 */
export default class PeriodReportCommentEditorWidget extends Widget {
	constructor({
		accessor = () => {},
		mutator = () => {},
		avatarUrl,
		showAvatar = false,
		onPictureUploadRequest,
		onSourceDeletionRequest,
		dataTestName = "",
		defaultTexts,
		inputValueActionBuilder,
	}) {
		super(...arguments);
		this._accessor = accessor;
		this._mutator = mutator;
		this._avatarUrl = avatarUrl;
		this._showAvatar = showAvatar;
		this._onPictureUploadRequest = onPictureUploadRequest;
		this._onSourceDeletionRequest = onSourceDeletionRequest;
		this._dataTestName = dataTestName;

		this._inputValueActionBuilder = inputValueActionBuilder;
		this._makeWysiwygEditor({ defaultTexts });
	}

	setDefaultTexts(defaultTexts) {
		const sortedTexts = defaultTexts.sort((first, second) =>
			first.getLabel() > second.getLabel() ? 1 : -1,
		);

		this._makeWysiwygEditor({ defaultTexts: sortedTexts });

		this.update();
	}

	renderContentOn(html) {
		html.div(
			{
				klass: "period-report-comment period-report-comment-editor",
				"data-help": "period-report-comment-editor",
				"data-test": this._dataTestName,
			},
			this._avatarUrlRenderer(),
			this._wysiwygEditorWidget,
		);
	}

	insertComment(comment) {
		this._wysiwygEditorWidget.replaceSelectionWithHTML(comment);
	}

	_avatarUrlRenderer() {
		return (html) => {
			if (!this._showAvatar) {
				return;
			}

			const authorImageSource =
				this._avatarUrl || "/Client/img/avatarUrl-user-grey-lg.png";
			html.div({ klass: "avatar" }, html.img({ src: authorImageSource }));
		};
	}

	_makeWysiwygEditor({ defaultTexts }) {
		this._wysiwygEditorWidget = new WysiwygEditorWidget({
			content: this._accessor,
			onChange: this._mutator,
			defaultTexts,
			inputValueActionBuilder: this._inputValueActionBuilder,
			extraActions: this._makeExtraActions(),
		});
	}

	_makeExtraActions() {
		if (!this._onPictureUploadRequest) return [];

		return [
			new ImageAction({
				onPictureUploadRequest: this._onPictureUploadRequest,
				onSourceDeletionRequest: this._onSourceDeletionRequest,
			}),
		];
	}
}
